import { CheckIcon, PlusIcon } from '@heroicons/react/20/solid'
import { ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown, Loader2 } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import * as yup from 'yup'

import { Button } from '@/components/ui/button'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import MultipleSelector from '@/components/ui/multi-selector'
import { PaginationComponent } from '@/components/ui/pagination'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAppSelector } from '@/hooks'
import { cn } from '@/lib/utils'
import { Layout } from '@/pages/customer/layout'
import productService from '@/services/product-service'
import userService from '@/services/user-service'

import { DiscountShapeIcon, EditIcon, TrashIcon } from '@/assets/icons'
import { toZonedTime } from 'date-fns-tz'

export interface CreateProductForm {
	title: string
	stand_id: string
	manufacturer_name: string
	category_id: string
	quantity: number
	price: string
	description: string
	manufacturer_brand?: string
}

export interface ApplyDiscountForm {
	discount: string
	standId: { value: string; label: string }[]
}

export const Products = () => {
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')
	const auth = useAppSelector(state => state.auth)

	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [isCreateDialogVisible, setIsCreateDialogVisible] = useState(false)
	const [isUpdateDialogVisible, setIsUpdateDialogVisible] = useState<Product | null>(null)
	const [isDiscountDialogVisible, setIsDiscountDialogVisible] = useState(false)
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [page, setPage] = useState(1)
	const [selectedColumn, setSelectedColumn] = useState('')
	const [filterValue, setFilterValue] = useState('')
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')

	const searchableColumns = [
		{ label: 'Artikelnummer', value: 'sku' },
		{ label: 'Bezeichnung', value: 'title' },
	]

	const schema = yup.object<CreateProductForm>().shape({
		title: yup.string().required('Bezeichnung ist erforderlich!'),
		stand_id: yup.string().required('Regalnummer ist erforderlich!'),
		manufacturer_name: yup.string().notRequired(),
		manufacturer_brand: yup.string().notRequired(),
		quantity: yup
			.number()
			.transform((value, originalValue) => {
				return originalValue === '' ? undefined : value
			})
			.typeError('Die Menge muss eine Zahl sein')
			.required('Menge ist erforderlich!'),
		category_id: yup.string().required('Kategorie ist erforderlich!'),
		price: yup.string().required('Preis ist erforderlich!'),
		description: yup.string().notRequired(),
	})

	const discountSchema = yup.object<ApplyDiscountForm>().shape({
		discount: yup.string().required('Rabatt in Prozent ist erforderlich!'),
		standId: yup
			.array()
			.transform((value, originalValue) => {
				return Array.isArray(originalValue) && originalValue.length === 0 ? undefined : value
			})
			.of(
				yup.object({
					label: yup.string().required(),
					value: yup.string().required(),
				}),
			)
			.default([])
			.required('Regalnummer ist erforderlich!'),
	})

	const form = useForm<CreateProductForm>({
		resolver: yupResolver(schema as any),
		mode: 'all',
	})

	const discountForm = useForm<ApplyDiscountForm>({
		resolver: yupResolver(discountSchema),
		defaultValues: { discount: '0.2', standId: [] },
		mode: 'all',
	})

	const { data, isLoading, refetch } = useQuery(
		'fetch-user-products',
		() =>
			productService.fetchProductsByUser(auth.id, {
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
				filterColumn: selectedColumn,
				filterValue,
				sortColumn: sortColumn,
				sortType: sortDirection,
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching user bookings')
			},
		},
	)

	const { data: categories } = useQuery(
		'fetch-product-categories',
		() => productService.fetchProductCategories(),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching categories')
			},
		},
	)

	const { data: activeBookings } = useQuery(
		'fetch-user-active-bookings',
		() => userService.fetchUserActiveBookings(),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching bookings')
			},
		},
	)

	const { data: currentBookings } = useQuery(
		'fetch-user-current-bookings',
		() =>
			userService.fetchUserCurrentBookings({
				pageNumber: '1',
				pageSize: '10000',
			}),
		{
			refetchOnWindowFocus: false,
			enabled: isDiscountDialogVisible,
			onError: () => {
				toast.error('Error fetching bookings')
			},
		},
	)

	const { mutate: addProduct, isLoading: isAddingProduct } = useMutation({
		mutationKey: 'add-product',
		mutationFn: (data: any) => productService.addProduct(data),
	})

	const { mutate: updateProduct, isLoading: isUpdatingProduct } = useMutation({
		mutationKey: 'update-product',
		mutationFn: ({ productId, data }: { productId: Product['id']; data: any }) =>
			productService.updateProduct(productId, data),
	})

	const { mutate: deleteProduct } = useMutation({
		mutationKey: 'delete-product',
		mutationFn: (productId: Product['id']) => productService.deleteProduct(productId),
		onSuccess: () => {
			toast.success('Produkt erfolgreich gelöscht')
			refetch()
		},
		onError: () => {
			toast.error('Das Produkt konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.')
		},
	})

	const { mutate: addToPos, isLoading: isAddingToPos } = useMutation({
		mutationKey: 'add-to-pos',
		mutationFn: () => productService.addProductsToPos(),
		onSuccess: () => {
			toast.success(
				'Deine Artikel sind erfolgreich übermittelt worden und können nun im Store ausgedruckt werden!',
			)
			refetch()
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message ?? error?.request ?? error?.message)
		},
	})

	const { mutate: applyDiscount, isLoading: isApplyingDiscount } = useMutation({
		mutationKey: 'apply-discount',
		mutationFn: (data: { discount: string; standId: string[] }) =>
			productService.applyDiscount(auth.id, data),
	})

	useEffect(() => {
		if (sortColumn && sortDirection) {
			refetch()
		}
	}, [sortColumn, sortDirection])

	useEffect(() => {
		refetch()
	}, [rowsPerPage, page])

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

const generateSku = (standId: number, userId: number) => {
  const timeString = Date.now().toString()
  const timePortion = timeString.slice(-6)

  const parsedUserId = userId.toString().padStart(4, '0').slice(0, 4)
  const parsedStandId = standId.toString().padStart(3, '0').slice(0, 3)

  return parsedUserId + parsedStandId + timePortion
}


	const onSubmit = (data: CreateProductForm) => {
		const { category_id, ...rest } = data

		const standId = data.stand_id?.split(' ')?.[0]?.trim()
		const combinedDates = data.stand_id?.split(' ')?.[1]?.trim()
		const startDate = combinedDates?.split('+')?.[0]?.trim()
		const endDate = combinedDates?.split('+')?.[1]?.trim()

		let payload = {
			...rest,
			manufacturer: data.manufacturer_brand,
			brand_name: data.manufacturer_brand,
			sku: generateSku(Number(standId), auth.id),
			adaptive_price: '0',
			manual_price: '0',
			infinite_inventory: 0,
			user_id: auth.id,
			booking_id: activeBookings?.find(
				booking => booking.start === startDate && booking.end === endDate,
			),
			stand_id: standId,
			price: parseFloat(data.price.replace(',', '.')),
		}
		if (category_id) {
			payload = {
				...data,
				manufacturer: data.manufacturer_brand ?? '',
				brand_name: data.manufacturer_brand ?? '',
				sku: generateSku(Number(standId), auth.id),
				adaptive_price: '0',
				manual_price: '0',
				infinite_inventory: 0,
				user_id: auth.id,
				booking_id: activeBookings?.find(
					booking => booking.start === startDate && booking.end === endDate,
				)?.id as any,
				stand_id: standId,
				price: parseFloat(data.price.replace(',', '.')),
			}
		}
		if (isUpdateDialogVisible !== null) {
			updateProduct(
				{ productId: isUpdateDialogVisible.id, data: payload },
				{
					onSuccess: () => {
						toast.success('Produkt erfolgreich aktualisiert')
						form.reset({
							category_id: '',
							description: '',
							manufacturer_brand: '',
							manufacturer_name: '',
							price: '',
							quantity: '' as any,
							stand_id: '',
							title: '',
						})
						refetch()
						setIsUpdateDialogVisible(null)
					},
					onError: (error: any) => {
						toast.error(
							error?.response?.data?.message ??
								error?.request ??
								error?.message ??
								'Das Produkt kann nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
						)
					},
				},
			)
		} else {
			addProduct(payload, {
				onSuccess: () => {
					toast.success('Produkt wurde erfolgreich hinzugefügt')
					form.reset({
						category_id: '',
						description: '',
						manufacturer_brand: '',
						manufacturer_name: '',
						price: '',
						quantity: '' as any,
						stand_id: '',
						title: '',
					})
					refetch()
				},
				onError: (error: any) => {
					toast.error(
						error?.response?.data?.message ??
							error?.request ??
							error?.message ??
							'Produkt kann nicht hinzugefügt werden. Bitte versuchen Sie es erneut',
					)
				},
			})
		}
	}

	const onDiscountSubmit = (data: ApplyDiscountForm) => {
		const payload = { discount: data.discount, standId: data.standId.map(id => id.value) }
		applyDiscount(payload, {
			onSuccess: () => {
				toast.success('Discount applied successfully')
				discountForm.reset()
				setIsDiscountDialogVisible(false)
				refetch()
			},
			onError: (error: any) => {
				toast.error(
					error?.response?.data?.message ?? 'Failed to apply discount',
				)
			},
		})
	}

	const columns: ColumnDef<Product>[] = [
		{
			accessorKey: 'sku',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Artikelnummer" />,
		},
		{
			accessorKey: 'title',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Bezeichnung" />,
		},
		{
			accessorKey: 'brand_name',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Marke" />,
		},
		{
			accessorKey: 'quantity',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Menge" />,
		},
		{
			accessorKey: 'price',
			accessorFn: (row: Product) => {
				let price = row.price.toFixed(2)
				price = price.toLocaleString()
				let priceRaw = price.split('.')
				priceRaw[0] = priceRaw[0].replace(',', '.')
				price = priceRaw[0] + ',' + priceRaw[1]
				return `${price} €`
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Preis" />,
		},
		{
			accessorKey: 'is_added_to_pos',
			accessorFn: (row: Product) =>
				row.is_added_to_pos === 1 ? 'Aktiv' : row.is_added_to_pos === 2 ? 'Verkauft' : 'Neu',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						row.getValue('is_added_to_pos') === 'Aktiv'
							? 'bg-[#61B9641A] text-[#61B964]'
							: row.getValue('is_added_to_pos') === 'Neu'
								? 'bg-yellow-400/20 text-yellow-600'
								: 'bg-[#FFEFF2] text-[#DB5358]',
					)}>
					{row.getValue('is_added_to_pos')}
				</div>
			),
		},
		{
			accessorKey: 'inventory_change_timestamps',
			accessorFn: (row: Product) =>
				row.inventory_change_timestamps?.split(',').map((timestamp, index) => {
					const formattedDate = format(new Date(timestamp), 'dd.MM.yyyy')
					return (
						<div key={index}>
							{index + 1}. {formattedDate}
						</div>
					)
				}),
			cell: ({ row }) => row.getValue('inventory_change_timestamps'),
			header: ({ column }) => (
				<DataTableColumnHeader
					showInfoIcon
					showTooltip="Anzeige aktualisiert sich täglich um 01:00 Uhr"
					column={column}
					title="Verkaufsdatum"
				/>
			),
		},
		{
			accessorKey: 'createdAt',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Hinzugefügt" />,
			cell: ({ row }) => <div>{format(new Date(row.getValue('createdAt')), 'dd.MM.yyyy')}</div>,
		},
		{
			id: 'action-products',
			accessorKey: 'is_added_to_pos',
			header: ({ column }) => (
				<DataTableColumnHeader
					showInfoIcon
					showTooltip="Artikel können nur bearbeitet werden, wenn sie noch nicht bestätigt wurden"
					column={column}
					title="Aktionen"
				/>
			),
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					{row.original.is_added_to_pos === 0 && (
						<Tooltip>
							<TooltipTrigger
								onClick={() => {
									form.reset({
										title: row.original.title,
										category_id: row.original.category_id?.toString(),
										description: row.original.description,
										manufacturer_brand: row.original.manufacturer,
										manufacturer_name: row.original.manufacturer,
										price: row.original.price?.toString(),
										quantity: row.original.quantity,
										stand_id: `${row.original.stand_id?.toString()} ${row.original.booking.start}+${row.original.booking.end}`,
									})
									setIsUpdateDialogVisible(row.original)
								}}
								className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
								<EditIcon className="shrink-0" />
							</TooltipTrigger>
							<TooltipContent side="bottom">
								<p className="text-xs font-medium">Aktualisieren</p>
							</TooltipContent>
						</Tooltip>
					)}
					<Tooltip>
						<TooltipTrigger
							onClick={() => deleteProduct(row.original.id)}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<TrashIcon className="shrink-0" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Löschen</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	]

	const MobileUserProductsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('title')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Bezeichnung
								{sortColumn === 'title' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('is_added_to_pos')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Status
								{sortColumn === 'is_added_to_pos' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('inventory_change_timestamps')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								<Tooltip>
									<TooltipTrigger>Verkaufsdatum</TooltipTrigger>
									<TooltipContent side="top" className="max-w-[100px]">
										<p className="text-secondary/50 text-xs font-medium">
											Anzeige aktualisiert sich täglich um 01:00 Uhr
										</p>
									</TooltipContent>
								</Tooltip>
								{sortColumn === 'inventory_change_timestamps' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{data?.data?.map((product, index) => {
						let price = product.price.toFixed(2)
						price = price.toLocaleString()
						let priceRaw = price.split('.')
						priceRaw[0] = priceRaw[0].replace(',', '.')
						price = priceRaw[0] + ',' + priceRaw[1]
						price = `${price} €`
						return (
							<React.Fragment key={product.id.toString() + index.toString()}>
								<TableRow
									className={cn(
										'h-14',
										expandedRows.includes(product.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
									)}
									onClick={() => toggleRow(product.id)}>
									<TableCell
										align="left"
										className={cn('text-xs', {
											'text-muted-foreground': !expandedRows.includes(product.id),
										})}>
										<div className="flex items-center">
											<ChevronRightIcon
												className={cn(
													'size-3 shrink-0 -mt-0.5',
													expandedRows.includes(product.id)
														? 'rotate-90 text-secondary'
														: 'text-muted-foreground',
												)}
											/>
											{product.title}
										</div>
									</TableCell>
									<TableCell
										align="center"
										className={cn('text-[10px]', {
											'text-muted-foreground': !expandedRows.includes(product.id),
										})}>
										<div
											className={cn(
												'text-[10px] w-fit p-1.5 px-2 rounded-[18px]',
												product.is_added_to_pos === 1
													? 'bg-[#61B9641A] text-[#61B964]'
													: product.is_added_to_pos === 0
														? 'bg-yellow-400/20 text-yellow-600'
														: 'bg-[#FFEFF2] text-[#DB5358]',
											)}>
											{product.is_added_to_pos === 1
												? 'Aktiv'
												: product.is_added_to_pos === 2
													? 'Verkauft'
													: 'Neu'}
										</div>
									</TableCell>
									<TableCell
										align="center"
										className={cn('text-xs', {
											'text-muted-foreground': !expandedRows.includes(product.id),
										})}>
										{product.inventory_change_timestamps?.split(',').map((timestamp, index) => {
											const formattedDate = format(new Date(timestamp), 'dd.MM.yyyy')
											return (
												<div key={index}>
													{index + 1}. {formattedDate}
												</div>
											)
										})}
									</TableCell>
								</TableRow>
								{expandedRows.includes(product.id) && (
									<TableRow
										className={cn(
											'mt-2',
											!expandedRows.includes(product.id)
												? '!border-b-0'
												: 'border-b border-[#F4F4F4]',
										)}>
										<TableCell className="pt-0" colSpan={3}>
											<div className="px-3 flex flex-col gap-4">
												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">
														Hinzugefügt
													</h6>
													<p className="text-secondary text-xs font-medium">
														{format(new Date(product.createdAt), 'dd.MM.yyyy')}
													</p>
												</div>

												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">Marke</h6>
													<p className="text-secondary text-xs font-medium">{product.brand_name}</p>
												</div>

												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">Preis</h6>
													<p className="text-secondary text-xs font-medium">{price}</p>
												</div>
												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">
														Bezeichnung
													</h6>
													<p className="text-secondary text-xs font-medium">{product.title}</p>
												</div>
												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">Menge</h6>
													<p className="text-secondary text-xs font-medium">{product.quantity}</p>
												</div>
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-1">
														<Tooltip>
															<TooltipTrigger>
																<InformationCircleIcon className="text-muted-foreground size-3" />
															</TooltipTrigger>
															<TooltipContent className="max-w-[100px]">
																<p className="text-xs text-secondary/50 font-medium">
																	Artikel können nur bearbeitet werden, wenn sie noch nicht
																	bestätigt wurden
																</p>
															</TooltipContent>
														</Tooltip>
														<h6 className="text-muted-foreground text-[10px] font-medium">
															Aktion
														</h6>
													</div>
													<div className="flex items-center gap-1">
														{product.is_added_to_pos === 0 && (
															<div
																onClick={() => {
																	form.reset({
																		title: product.title,
																		category_id: product.category_id?.toString(),
																		description: product.description,
																		manufacturer_brand: product.manufacturer,
																		manufacturer_name: product.manufacturer,
																		price: product.price?.toString(),
																		quantity: product.quantity,
																		stand_id: `${product.stand_id?.toString()} ${product.booking.start}+${product.booking.end}`,
																	})
																	setIsUpdateDialogVisible(product)
																}}
																className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
																<EditIcon className="shrink-0 max-lg:size-3" />
															</div>
														)}
														<div
															onClick={() => deleteProduct(product.id)}
															className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
															<TrashIcon className="shrink-0 max-lg:size-3" />
														</div>
													</div>
												</div>
											</div>
										</TableCell>
									</TableRow>
								)}
							</React.Fragment>
						)
					})}
				</TableBody>
			</Table>
		)
	}

	return (
		<Layout title="Artikel">
			<ResponsiveModal
				open={isUpdateDialogVisible !== null || isCreateDialogVisible}
				onOpenChange={() => {
					form.reset({
						category_id: '',
						description: '',
						manufacturer_brand: '',
						manufacturer_name: '',
						price: '',
						quantity: '' as any,
						stand_id: '',
						title: '',
					})
					setIsUpdateDialogVisible(null)
					setIsCreateDialogVisible(false)
				}}>
				<ResponsiveModalContent className="gap-0">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							{isUpdateDialogVisible !== null ? 'Produkt aktualisieren' : 'Produkt hinzufügen'}
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							{isUpdateDialogVisible !== null ? 'Produkt aktualisieren' : 'Produkt hinzufügen'}
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="grid grid-cols-1 items-start lg:grid-cols-2 mt-6 gap-y-6 lg:mt-10 lg:gap-x-4 lg:gap-y-5">
								<FormField
									control={form.control}
									name="title"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Bezeichnung <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Art, Farbe, Größe: Beispiel: Body, Blau, 52"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="stand_id"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Regalnummer <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<Select onValueChange={field.onChange} value={field.value}>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder="Regalnummer" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{activeBookings &&
														activeBookings.length > 0 &&
														activeBookings.map(booking => (
															<SelectItem
																key={'stand_' + booking.stand_id}
																value={`${booking.stand_id.toString()} ${booking.start}+${booking.end}`}>
																{booking.stand?.label} (
																{format(toZonedTime(new Date(booking.start), 'UTC'), 'dd.MM.yyyy')}{' '}
																- {format(toZonedTime(new Date(booking.end), 'UTC'), 'dd.MM.yyyy')})
															</SelectItem>
														))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="category_id"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Kategorie <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<Select onValueChange={field.onChange} value={field.value}>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder="Kategorie" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{categories &&
														categories.length > 0 &&
														categories.map(category => (
															<SelectItem
																key={'category_' + category.id}
																value={category.id.toString()}>
																{category.name}
															</SelectItem>
														))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="manufacturer_brand"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">Marke</FormLabel>
											<FormControl>
												<Input placeholder="Marke" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="quantity"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Menge <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													inputMode="numeric"
													placeholder="Menge"
													{...field}
													onChange={e => {
														const value = e.target.value
														if (/^\d*\.?\d*$/.test(value)) {
															field.onChange(value)
														}
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="price"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Preis <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Preis" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="description"
									render={({ field }) => (
										<FormItem className="w-full lg:col-span-2">
											<FormLabel className="text-muted-foreground">
												Besonderheiten (Diese Informationen erscheinen nicht auf dem Etikett)
											</FormLabel>
											<FormControl>
												<Textarea className="resize-none min-h-[117px]" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								variant="secondary"
								className="mt-20 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{(isAddingProduct || isUpdatingProduct) && <Loader2 className="animate-spin" />}
								{isAddingProduct || isUpdatingProduct
									? 'Bitte warten'
									: isUpdateDialogVisible !== null
										? 'Produkt aktualisieren'
										: 'Produkt Hinzufügen'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>

			<ResponsiveModal
				open={isDiscountDialogVisible}
				onOpenChange={() => {
					discountForm.reset()
					setIsDiscountDialogVisible(false)
				}}>
				<ResponsiveModalContent className="gap-0 !max-w-[560px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Um wie viel Prozent möchtest du deine Artikel rabattieren?
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Um wie viel Prozent möchtest du deine Artikel rabattieren?
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...discountForm}>
						<form onSubmit={discountForm.handleSubmit(onDiscountSubmit)}>
							<div className="grid grid-cols-1 items-start mt-6 gap-y-6 lg:mt-10 lg:gap-x-4 lg:gap-y-5">
								<FormField
									control={discountForm.control}
									name="standId"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">Regal</FormLabel>
											<FormControl>
												<MultipleSelector
													{...field}
													placeholder="Wähle dein Regal"
													hideClearAllButton
													badgeClassName="bg-white text-black border border-black/20"
													hidePlaceholderWhenSelected
													options={
														currentBookings && currentBookings.length > 0
															? currentBookings.map(booking => ({
																	label: booking.stand_id.toString(),
																	value: booking.stand_id.toString(),
																}))
															: []
													}
													emptyIndicator={
														<p className="text-center text-xs lg:text-sm pt-1 leading-10 text-gray-600 dark:text-gray-400">
															No results found.
														</p>
													}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={discountForm.control}
									name="discount"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">Rabatt in Prozent</FormLabel>
											<Select onValueChange={field.onChange} value={field.value}>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder="" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													<SelectItem value="0.2">20 %</SelectItem>
													<SelectItem value="0.3">30 %</SelectItem>
													<SelectItem value="0.5">50 %</SelectItem>
													<SelectItem value="0.8">80 %</SelectItem>
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								variant="secondary"
								className="mt-20 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{isApplyingDiscount && <Loader2 className="animate-spin" />}
								{isApplyingDiscount ? 'Bitte warten' : 'Rabatt Bestätigen'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="mt-3 rounded-[16px] px-[18px] py-5 bg-white flex flex-col gap-4">
				<div className="flex items-center gap-10 justify-between">
					<div className="flex items-center gap-2 max-lg:justify-between max-lg:w-full lg:gap-3">
						<h1 className="text-secondary text-xs lg:text-sm 2xl:text-base font-medium">
							Neuen Artikel hinzufügen
						</h1>
						<Button
							size="icon"
							onClick={() => setIsCreateDialogVisible(true)}
							variant="secondary"
							className="size-8 shrink-0 rounded-lg bg-secondary">
							<PlusIcon className="size-5 text-white" />
						</Button>
					</div>

					<div className="flex shrink max-lg:hidden items-center gap-4">
						<Button
							variant="outline"
							disabled={isAddingToPos}
							onClick={() => addToPos()}
							className="text-secondary whitespace-normal max-lg:text-xs max-lg:h-10 font-normal border-secondary rounded-lg lg:rounded-xl">
							<CheckIcon className="size-4 lg:size-6 shrink-0" />
							ALLE ARTIKEL HINZUGEFÜGT? HIER BESTÄTIGEN
						</Button>
						<Button
							variant="outline"
							onClick={() => {
								setIsDiscountDialogVisible(true)
							}}
							className="text-secondary whitespace-normal max-lg:text-xs max-lg:h-10 font-normal border-secondary rounded-lg lg:rounded-xl">
							<DiscountShapeIcon className="size-4 lg:size-6 shrink-0" />
							ALLE ARTIKEL RABATTIEREN
						</Button>
					</div>
				</div>
				<div className="flex max-lg:mt-1 mt-6 flex-col gap-3 lg:gap-4">
					<div className="flex items-stretch justify-center lg:hidden gap-2">
						<Button
							variant="outline"
							disabled={isAddingToPos}
							onClick={() => addToPos()}
							className="text-secondary sm:w-full grow p-1.5 whitespace-normal max-sm:text-[10px] max-lg:text-xs max-lg:min-h-10 font-normal border-secondary rounded-lg lg:rounded-xl">
							ALLE ARTIKEL HINZUGEFÜGT? HIER BESTÄTIGEN
						</Button>
						<Button
							variant="outline"
							onClick={() => setIsDiscountDialogVisible(true)}
							className="text-secondary sm:w-full shrink p-1.5 whitespace-normal max-sm:text-[10px] max-lg:text-xs max-lg:min-h-10 font-normal border-secondary rounded-lg lg:rounded-xl">
							ALLE ARTIKEL RABATTIEREN
						</Button>
					</div>
					<div className="flex max-lg:hidden items-center gap-3">
						<Select value={selectedColumn} onValueChange={setSelectedColumn}>
							<SelectTrigger className="flex-1">
								<SelectValue placeholder="Kategorie" />
							</SelectTrigger>
							<SelectContent>
								{searchableColumns.map(column => (
									<SelectItem key={column.value} value={column.value}>
										{column.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Input
							value={filterValue}
							onChange={e => setFilterValue(e.target.value)}
							className="flex-1"
							placeholder="Bezeichnung eingeben"
						/>
						<Button
							onClick={() => refetch()}
							variant="secondary"
							className="w-[90px] font-medium bg-secondary rounded-xl">
							Suche
						</Button>
					</div>
					<div className="lg:mt-6 flex justify-end">
						<Select
							value={rowsPerPage.toString()}
							onValueChange={value => setRowsPerPage(Number(value))}>
							<SelectTrigger className="w-[133px] h-12 text-xs">
								<SelectValue placeholder="zeige 50" />
							</SelectTrigger>
							<SelectContent>
								{[50, 100, 'Alle'].map(pageSize => (
									<SelectItem
										key={pageSize}
										value={pageSize === 'Alle' ? '20000' : pageSize.toString()}>
										zeige {pageSize === '20000' ? 'Alle' : pageSize}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="lg:hidden flex flex-col gap-3">
						<Select value={selectedColumn} onValueChange={setSelectedColumn}>
							<SelectTrigger className="h-10 text-xs w-full">
								<SelectValue placeholder="Kategorie" />
							</SelectTrigger>
							<SelectContent>
								{searchableColumns.map(column => (
									<SelectItem key={column.value} value={column.value}>
										{column.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<div className="flex lg:hidden items-center gap-2">
							<Input
								value={filterValue}
								onChange={e => setFilterValue(e.target.value)}
								className="h-10 text-xs"
								placeholder="Bezeichnung eingeben"
							/>
							<Button
								onClick={() => refetch()}
								variant="secondary"
								className="w-[73px] py-3 text-xs font-medium bg-secondary rounded-lg">
								Suche
							</Button>
						</div>
					</div>
				</div>
				{isSmallScreen ? (
					<MobileUserProductsTable />
				) : (
					<DataTable columns={columns} data={data?.data ?? []} isLoading={isLoading} />
				)}
				<div className="mt-6 lg:mt-16 flex justify-center">
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
				</div>
			</div>
		</Layout>
	)
}
